
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Trash2, Send } from "lucide-react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NewsletterAdmin = () => {
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriberToDelete, setSubscriberToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("newsletter_subscribers")
        .select("*, blog_posts(title)")
        .order("subscribed_at", { ascending: false });

      if (error) throw error;
      
      setSubscribers(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load subscribers",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSubscriber = async () => {
    if (!subscriberToDelete) return;

    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .delete()
        .eq("id", subscriberToDelete);

      if (error) throw error;

      toast({
        title: "Subscriber removed",
        description: "The subscriber has been successfully removed.",
      });

      await fetchSubscribers();
    } catch (error: any) {
      toast({
        title: "Failed to remove subscriber",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubscriberToDelete(null);
    }
  };

  const activeSubscribers = subscribers.filter(
    (subscriber) => !subscriber.unsubscribed_at
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Newsletter Subscribers</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchSubscribers()}
          className="flex items-center gap-2"
        >
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Subscribers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSubscribers.length}</div>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse">Loading subscribers...</div>
        </div>
      ) : subscribers.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Subscriber List</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Subscribed Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map((subscriber) => (
                  <TableRow key={subscriber.id}>
                    <TableCell className="font-medium">
                      {subscriber.email}
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(subscriber.subscribed_at),
                        "MMM d, yyyy"
                      )}
                    </TableCell>
                    <TableCell>
                      {subscriber.source_blog_id
                        ? subscriber.blog_posts?.title || "Blog post"
                        : "General"}
                    </TableCell>
                    <TableCell>
                      {subscriber.unsubscribed_at ? (
                        <span className="text-red-500">Unsubscribed</span>
                      ) : (
                        <span className="text-green-500">Active</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSubscriberToDelete(subscriber.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="p-8 text-center">
          <div className="flex justify-center mb-4">
            <User className="h-12 w-12 text-gray-300" />
          </div>
          <h3 className="text-lg font-medium mb-2">No subscribers yet</h3>
          <p className="text-gray-500 mb-4">
            No one has subscribed to your newsletter yet.
          </p>
        </Card>
      )}

      <AlertDialog open={!!subscriberToDelete} onOpenChange={() => setSubscriberToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this subscriber from your newsletter list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSubscriber}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewsletterAdmin;
